// Silent field awareness.
//
// The resume space is crowded and most tools are built, implicitly, for
// software engineers - they reward "impact metrics" and "cross-functional
// leadership" and quietly mangle a resume that does not speak that dialect.
// This product's field selector defaulted to "technology" and was never wired
// into the tailoring at all, so every resume - a nurse's, a welder's, a
// teacher's - was tailored as if it were a developer's.
//
// This fixes that without making anyone announce who they are: the field is
// detected from the resume and the posting, and a domain profile shapes what
// the tailor emphasizes and how it phrases things. Inclusive by construction -
// every field maps to a real profile, and an unknown one falls to a solid
// generic. The goal is that a home-health aide and a backend engineer each get
// a resume tailored in their own field's language, from the same front door.

export type FieldKey =
  | 'technology'
  | 'healthcare'
  | 'trades'
  | 'finance'
  | 'education'
  | 'government'
  | 'sales_marketing'
  | 'operations'
  | 'creative'
  | 'science'
  | 'legal'
  | 'hospitality'
  | 'generic';

interface FieldProfile {
  label: string;
  /** Lowercase signals that this field is in play, weighted by specificity. */
  keywords: string[];
  /** Injected into the tailoring prompt. What a strong resume in this field does. */
  guidance: string;
}

// Order matters only for readability; detection scores all of them.
const PROFILES: Record<Exclude<FieldKey, 'generic'>, FieldProfile> = {
  healthcare: {
    label: 'Healthcare & Nursing',
    keywords: [
      'patient', 'clinical', 'rn', 'lpn', 'bsn', 'cna', 'nurse', 'nursing', 'hospital', 'unit',
      'emr', 'ehr', 'epic', 'cerner', 'hipaa', 'triage', 'icu', 'er', 'acute', 'ambulatory',
      'medication', 'care plan', 'vitals', 'phlebotomy', 'caregiver', 'home health', 'hospice',
      'bls', 'acls', 'pals', 'medical', 'physician', 'therapy', 'pharmacy', 'diagnosis',
    ],
    guidance: `This is a HEALTHCARE resume. What matters here:
- Lead with licenses and certifications (RN, LPN, BLS, ACLS, specialty certs) - they are pass/fail gates, not nice-to-haves. Never bury or drop them.
- Keep clinical specifics: care setting (ICU, med-surg, ambulatory), patient population, acuity, patient load or ratios, EMR system (Epic, Cerner).
- Value here is patient outcomes, safety, and compliance (HIPAA, infection control) - not "business impact" or revenue.
- Do not invent patient counts, ratios, or outcome percentages; a clinician is accountable for every claim.`,
  },
  trades: {
    label: 'Skilled Trades & Field Work',
    keywords: [
      'welding', 'welder', 'forklift', 'osha', 'journeyman', 'apprentice', 'apprenticeship',
      'hvac', 'electrician', 'plumbing', 'plumber', 'cdl', 'blueprint', 'machinist', 'fabrication',
      'construction', 'carpenter', 'diesel', 'hydraulic', 'pipefitter', 'millwright', 'rigging',
      'preventive maintenance', 'lockout', 'tagout', 'calibration', 'ppe', 'crane', 'excavator',
      'warehouse', 'shift', 'inventory', 'logistics', 'assembly',
    ],
    guidance: `This is a SKILLED TRADES / field-work resume. What matters here:
- Certifications and tickets are gates: OSHA, CDL class, welding certs (AWS), journeyman/apprentice status, equipment licenses. Keep every one, verbatim.
- Keep the concrete: equipment and tools operated, systems maintained, safety record, hours/shifts, throughput, adherence to spec.
- Value here is reliability, safety, and precision - "zero recordable incidents", "on-schedule", "to blueprint" - not corporate-speak. Do NOT inflate a hands-on task into a management claim.
- Plain, direct verbs. A recruiter here distrusts a resume that sounds like a consultant wrote it.`,
  },
  government: {
    label: 'Government & Public Sector',
    keywords: [
      'gs-', 'usajobs', 'federal', 'clearance', 'secret', 'ts/sci', 'public sector', 'municipal',
      'county', 'state agency', 'compliance', 'policy', 'regulation', 'grant', 'procurement',
      ' far', 'dfars', 'contracting officer', 'veteran', 'dod', 'gsa', 'title', 'statute',
    ],
    guidance: `This is a GOVERNMENT / public-sector resume. What matters here:
- Federal and public-sector screening is literal: mirror the posting's exact language and required qualifications, and keep clearances, GS levels, and certifications explicit.
- Detail is a feature, not a flaw - these resumes are longer and specific on purpose. Do not over-compress or drop duties to look sleek.
- Keep policy, compliance, regulation, grant, and procurement specifics intact.
- Value is stewardship, compliance, and public outcomes served, not growth metrics.`,
  },
  education: {
    label: 'Education & Teaching',
    keywords: [
      'teacher', 'teaching', 'classroom', 'curriculum', 'students', 'lesson', 'k-12', 'grade',
      'iep', 'pedagogy', 'instruction', 'assessment', 'faculty', 'professor', 'lecturer', 'tutor',
      'certification', 'licensure', 'esl', 'special education', 'district', 'principal', 'academic',
      'enrollment', 'literacy', 'stem education',
    ],
    guidance: `This is an EDUCATION resume. What matters here:
- Keep certifications and licensure (state teaching license, subject endorsements, grade bands) - they gate eligibility.
- Value is student outcomes, growth, and engagement (test-score gains, graduation, differentiated instruction, IEP compliance), plus classroom and curriculum specifics.
- Keep the setting: grade level, subject, class size, student population, district or institution type.
- Do not manufacture score gains or student counts.`,
  },
  finance: {
    label: 'Finance & Accounting',
    keywords: [
      'accounting', 'accountant', 'audit', 'gaap', 'cpa', 'cfa', 'financial', 'budget', 'forecast',
      'reconciliation', 'ledger', 'p&l', 'variance', 'controller', 'treasury', 'tax', 'sox',
      'ap', 'ar', 'payroll', 'fp&a', 'valuation', 'underwriting', 'portfolio', 'compliance',
      'erp', 'sap', 'quickbooks', 'excel', 'financial modeling',
    ],
    guidance: `This is a FINANCE / accounting resume. What matters here:
- Precision and credentials: keep CPA, CFA, licenses, and the exact systems (SAP, Oracle, QuickBooks, Excel modeling).
- Value is accuracy, controls, and dollars managed or saved - budgets owned, variances closed, audits passed, close cycles shortened. Keep real figures; never invent one.
- Keep regulatory and controls specifics (GAAP, SOX, tax).
- Understated and exact beats grandiose here - inflating a reconciliation into "financial strategy leadership" reads as a red flag.`,
  },
  sales_marketing: {
    label: 'Sales & Marketing',
    keywords: [
      'sales', 'quota', 'pipeline', 'revenue', 'leads', 'crm', 'salesforce', 'hubspot', 'marketing',
      'campaign', 'seo', 'sem', 'brand', 'demand generation', 'conversion', 'acquisition', 'churn',
      'account', 'territory', 'b2b', 'b2c', 'go-to-market', 'social media', 'content', 'roi', 'ctr',
    ],
    guidance: `This is a SALES / marketing resume. What matters here:
- This is the one field where numbers ARE the resume: quota attainment, revenue, pipeline, conversion, growth - but only the candidate's real ones. Never invent a percentage or a dollar figure, and never lift a target from the job posting as if it were an achievement.
- Keep tools and channels (Salesforce, HubSpot, the specific channels) and the market (B2B/B2C, segment, territory).
- Strong, outcome-led verbs are appropriate here - but a fabricated metric is the fastest way to lose a role in a reference check.`,
  },
  operations: {
    label: 'Operations & Logistics',
    keywords: [
      'operations', 'logistics', 'supply chain', 'scheduling', 'vendor', 'procurement', 'inventory',
      'process improvement', 'lean', 'six sigma', 'kpi', 'throughput', 'fulfillment', 'warehouse',
      'distribution', 'kaizen', 'sop', 'workflow', 'coordination', 'shift', 'facility', 'dispatch',
    ],
    guidance: `This is an OPERATIONS / logistics resume. What matters here:
- Value is efficiency, reliability, and scale run: teams coordinated, throughput, cost reduced, cycle time cut, on-time rates, process improvements - with the candidate's own numbers only.
- Keep the concrete: systems (ERP, WMS), methodologies (Lean, Six Sigma) where genuinely used, scope (sites, headcount, volume).
- Reframe duties in the posting's language, but do not inflate coordinating a team into "owning" a function the source does not show.`,
  },
  creative: {
    label: 'Creative & Design',
    keywords: [
      'design', 'designer', 'ux', 'ui', 'figma', 'adobe', 'photoshop', 'illustrator', 'branding',
      'portfolio', 'creative', 'art director', 'typography', 'motion', 'video', 'copywriting',
      'visual', 'prototyping', 'wireframe', 'user research', 'content design',
    ],
    guidance: `This is a CREATIVE / design resume. What matters here:
- Keep tools and craft (Figma, Adobe suite, prototyping, motion) and the type of work (brand, product, editorial).
- Value is the work's outcome and the candidate's role in it - what they designed, for whom, and the result - plus collaboration with product/eng/marketing.
- Portfolio-adjacent: keep concrete deliverables. Avoid vague "creative visionary" puffery a hiring manager will discount.`,
  },
  science: {
    label: 'Science & Research',
    keywords: [
      'research', 'laboratory', 'lab', 'experiment', 'assay', 'phd', 'publication', 'peer-reviewed',
      'protocol', 'clinical trial', 'data analysis', 'hypothesis', 'biology', 'chemistry', 'physics',
      'r&d', 'pipeline', 'gmp', 'glp', 'spectroscopy', 'sequencing', 'statistics', 'grant',
    ],
    guidance: `This is a SCIENCE / research resume. What matters here:
- Keep publications, techniques, instruments, and protocols - these are the credentials, and a Publications section must survive intact.
- Value is rigor and contribution: methods run, findings, techniques mastered, grants, compliance (GMP/GLP).
- Precise and specific beats flashy. Do not overstate a contribution to a study or invent results.`,
  },
  legal: {
    label: 'Legal',
    keywords: [
      'legal', 'attorney', 'paralegal', 'litigation', 'contract', 'compliance', 'counsel', 'juris',
      'jd', 'bar', 'discovery', 'deposition', 'brief', 'regulatory', 'due diligence', 'clerk',
      'statute', 'case', 'filing', 'intellectual property', 'corporate law',
    ],
    guidance: `This is a LEGAL resume. What matters here:
- Keep bar admission, jurisdiction, and practice areas explicit - they gate eligibility.
- Value is matters handled, practice areas, and outcomes, with precise, conservative language. Keep the specifics (litigation, contracts, regulatory, IP).
- Understatement is the norm; inflating a supporting role into lead counsel is a serious misrepresentation here.`,
  },
  hospitality: {
    label: 'Hospitality & Service',
    keywords: [
      'restaurant', 'hospitality', 'server', 'bartender', 'kitchen', 'chef', 'cook', 'hotel',
      'guest', 'front desk', 'food', 'beverage', 'catering', 'banquet', 'housekeeping', 'barista',
      'pos', 'covers', 'shift', 'customer service', 'reservations', 'culinary',
    ],
    guidance: `This is a HOSPITALITY / service resume. What matters here:
- Value is guest experience, volume handled, speed, and reliability: covers per shift, guest satisfaction, upsell, teamwork under pressure.
- Keep the concrete: venue type and size, service style, POS systems, food-safety certs (ServSafe), languages.
- Plain, energetic verbs. Keep it grounded - do not dress a line-cook duty up as "culinary operations leadership".`,
  },
  technology: {
    label: 'Technology & Software',
    keywords: [
      'software', 'engineer', 'developer', 'python', 'java', 'javascript', 'typescript', 'react',
      'kubernetes', 'aws', 'azure', 'gcp', 'devops', 'api', 'microservices', 'ci/cd', 'sql',
      'docker', 'terraform', 'backend', 'frontend', 'full-stack', 'sre', 'agile', 'git', 'cloud',
      'machine learning', 'data engineering',
    ],
    guidance: `This is a TECHNOLOGY resume. What matters here:
- Keep the stack exact and specific: languages, frameworks, cloud, tools (names, not categories).
- Value is systems built, scale handled, reliability, and shipped outcomes - with the candidate's real numbers only.
- Reframe in the posting's terminology, but never claim a technology the source resume does not show.`,
  },
};

const GENERIC_GUIDANCE = `Tailor to this candidate's own field, whatever it is. What matters in every field:
- Keep licenses, certifications, and credentials verbatim - in many fields they are eligibility gates, not extras.
- Keep the concrete specifics of the work: setting, scope, tools, and the candidate's own numbers. Never invent a metric, a credential, or a responsibility.
- Reframe real duties in the job posting's language; do not manufacture importance or seniority the source does not show.
- Match the field's register: some fields reward strong outcome-led verbs, others reward precise understatement. Read the resume and the posting and match their tone.`;

/**
 * Detect the candidate's field from the resume and the posting, silently.
 *
 * Deterministic keyword scoring - no model call, no cost, same answer every
 * time. The resume is weighted over the posting: a nurse applying to a role a
 * hospital wrote in HR-speak is still a nurse. A field must clear a small floor
 * of evidence to win; otherwise it falls to the generic profile, which is
 * itself solid, so an unrecognized field is served, never mistailored.
 */
export function detectField(resumeText: string, jobDescription = ''): FieldKey {
  const hay = ` ${resumeText.toLowerCase()} ${resumeText.toLowerCase()} ${jobDescription.toLowerCase()} `;
  let best: { key: FieldKey; score: number } = { key: 'generic', score: 0 };
  for (const [key, profile] of Object.entries(PROFILES) as Array<[FieldKey, FieldProfile]>) {
    let score = 0;
    for (const kw of profile.keywords) {
      // Whole-token-ish match so "er" inside "server" does not fire "rn".
      if (hay.includes(` ${kw} `) || hay.includes(`${kw},`) || hay.includes(`${kw}.`) || hay.includes(`${kw}s `)) {
        score += kw.length > 6 ? 2 : 1; // specific terms count double
      }
    }
    if (score > best.score) best = { key, score };
  }
  // Below this the signal is coincidental; serve the inclusive generic profile.
  return best.score >= 3 ? best.key : 'generic';
}

/** The tailoring guidance for a field. Always returns something usable. */
export function fieldGuidance(key: FieldKey): string {
  if (key === 'generic') return GENERIC_GUIDANCE;
  return PROFILES[key]?.guidance ?? GENERIC_GUIDANCE;
}

/** Detect and return guidance in one step - what the tailor calls. */
export function guidanceFor(resumeText: string, jobDescription = ''): string {
  return fieldGuidance(detectField(resumeText, jobDescription));
}
